import {
	EngagementDto,
	PageDto,
	SearchFilter,
	PageOptionsDto,
	LanguageFilter,
	ProvidersFilter,
	ON_SURVEY_TRACKING_EVENT,
	ON_SURVEY_EVENT,
	ON_SURVEY_ANSWER_EVENT,
	JOIN_SURVEY_EVENTS_ROOM,
	EngagementPopulatedField,
	LanguageDistributionDto,
	BarChartDto,
	DistrictFilter,
	LanguagePerProvidersDto,
	EngagementStatsDto,
	EngagementChartTypeEnum,
	ProviderChartTypeEnum,
	EngagementStatusDto,
} from '@families-link/shared';
import api from '.';
import { configService } from '../../modules/_core';
import { io } from 'socket.io-client';
import { RootState } from '..';
import { getLocalStorageDistrictId } from '../../modules/_core/utils';

const engagementsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getEngagementsList: builder.query<
			PageDto<EngagementDto>,
			PageOptionsDto &
				SearchFilter &
				LanguageFilter &
				ProvidersFilter & {
					district?: string | null;
					start?: string | null;
					end?: string | null;
					archived?: boolean;
					schools?: string;
					takeAll?: boolean;
				}
		>({
			query: ({
				page,
				take,
				orderField,
				order,
				search,
				languages,
				providers,
				district,
				start,
				end,
				archived,
				schools,
				takeAll,
			}) => ({
				url: `engagements`,
				params: {
					page,
					take,
					orderField,
					order,
					search,
					languages,
					providers,
					district,
					start,
					end,
					archived,
					schools,
					takeAll,
				},
			}),
			providesTags: ['Engagement', 'Survey', 'SurveyAnswer', 'EngagementStatus'],
			async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState, dispatch }) {
				const state = getState() as RootState;
				const token = state.session.access?.token;

				if (!token) {
					console.error('No access token found');

					return;
				}

				const socket = io(configService.get('NX_PUBLIC_APP_API_URL'), {
					auth: { token },
					transports: ['websocket', 'polling'],
				});

				socket.on('connect', () => {
					console.log('WebSocket connected!');
				});

				const districtId = getLocalStorageDistrictId();

				socket.emit(JOIN_SURVEY_EVENTS_ROOM, { districtId });

				const eventListner = (data: any) => {
					dispatch(engagementsApi.util.invalidateTags([{ type: 'Survey' }]));
				};

				socket.on(ON_SURVEY_EVENT, eventListner);
				socket.on(ON_SURVEY_TRACKING_EVENT, eventListner);
				socket.on(ON_SURVEY_ANSWER_EVENT, eventListner);

				try {
					await cacheEntryRemoved;
				} finally {
					socket.off(ON_SURVEY_EVENT, eventListner);
					socket.off(ON_SURVEY_TRACKING_EVENT, eventListner);
					socket.off(ON_SURVEY_ANSWER_EVENT, eventListner);
					socket.disconnect();
				}
			},
		}),
		getEngagementsListUnified: builder.query<
			PageDto<EngagementDto>,
			PageOptionsDto &
				SearchFilter &
				LanguageFilter &
				ProvidersFilter & {
					district?: string | null;
					start?: string | null;
					end?: string | null;
					archived?: boolean;
					schools?: string;
					takeAll?: boolean;
				}
		>({
			query: ({
				page,
				take,
				orderField,
				order,
				search,
				languages,
				providers,
				district,
				start,
				end,
				archived,
				schools,
				takeAll,
			}) => ({
				url: `engagements/all/admin`,
				params: {
					page,
					take,
					orderField,
					order,
					search,
					languages,
					providers,
					district,
					start,
					end,
					archived,
					schools,
					takeAll,
				},
			}),
			providesTags: ['Engagement', 'Survey', 'SurveyAnswer', 'EngagementStatus'],
			async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState, dispatch }) {
				const state = getState() as RootState;
				const token = state.session.access?.token;

				if (!token) {
					console.error('No access token found');

					return;
				}

				const socket = io(configService.get('NX_PUBLIC_APP_API_URL'), {
					auth: { token },
					transports: ['websocket', 'polling'],
				});

				socket.on('connect', () => {
					console.log('WebSocket connected!');
				});

				const districtId = getLocalStorageDistrictId();

				socket.emit(JOIN_SURVEY_EVENTS_ROOM, { districtId });

				const eventListner = (data: any) => {
					dispatch(engagementsApi.util.invalidateTags([{ type: 'Survey' }]));
				};

				socket.on(ON_SURVEY_EVENT, eventListner);
				socket.on(ON_SURVEY_TRACKING_EVENT, eventListner);
				socket.on(ON_SURVEY_ANSWER_EVENT, eventListner);

				try {
					await cacheEntryRemoved;
				} finally {
					socket.off(ON_SURVEY_EVENT, eventListner);
					socket.off(ON_SURVEY_TRACKING_EVENT, eventListner);
					socket.off(ON_SURVEY_ANSWER_EVENT, eventListner);
					socket.disconnect();
				}
			},
		}),
		getEngagement: builder.query<
			EngagementDto,
			{ id?: string | null; populatedFields?: Array<EngagementPopulatedField> }
		>({
			query: ({ id, populatedFields }) => ({
				url: `engagements/${id}`,
				params: { populatedFields },
			}),
			providesTags: ['Engagement', 'EngagementStatus'],
		}),
		generateEngagementsCustomFieldFileUrl: builder.mutation<{ url: string }, FormData>({
			query: (data) => ({
				url: `engagements/file-url`,
				method: 'POST',
				body: data,
			}),
		}),
		createEngagement: builder.mutation<EngagementDto, any>({
			query: (data) => ({
				url: `engagements`,
				method: 'POST',
				body: data,
			}),
			invalidatesTags: ['Engagement'],
		}),
		createEngagementsArray: builder.mutation<{ success: any[]; failed: any[] }, any>({
			query: (data) => ({
				url: `engagements/array`,
				method: 'POST',
				body: data,
			}),
			invalidatesTags: ['Engagement'],
		}),
		updateEngagement: builder.mutation<EngagementDto, { id?: string; data: any }>({
			query: ({ id, data }) => ({
				url: `engagements/${id}`,
				method: 'PUT',
				body: data,
			}),
			invalidatesTags: ['Engagement', 'Survey', 'SurveyAnswer'],
		}),
		deleteEngagement: builder.mutation<EngagementDto, { id: string }>({
			query: ({ id }) => ({
				url: `engagements/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Engagement'],
		}),
		archiveEngagement: builder.mutation<EngagementDto, { id: string }>({
			query: ({ id }) => ({
				url: `engagements/${id}/archive`,
				method: 'PUT',
			}),
			invalidatesTags: ['Engagement'],
		}),
		unarchiveEngagement: builder.mutation<EngagementDto, { id: string }>({
			query: ({ id }) => ({
				url: `engagements/${id}/unarchive`,
				method: 'PUT',
			}),
			invalidatesTags: ['Engagement'],
		}),
		getEngagementsByFamilyId: builder.query<
			PageDto<EngagementDto>,
			PageOptionsDto & {
				familyId?: string;
			}
		>({
			query: ({ familyId, page, take, orderField, order }) => ({
				url: `engagements/family/${familyId}`,
				params: {
					page,
					take,
					orderField,
					order,
				},
			}),
			providesTags: ['Engagement', 'EngagementStatus'],
		}),
		getEngagementsByProviderId: builder.query<
			PageDto<EngagementDto>,
			PageOptionsDto & {
				providerId?: string;
			}
		>({
			query: ({ providerId, page, take, orderField, order }) => ({
				url: `engagements/provider/${providerId}`,
				params: {
					page,
					take,
					orderField,
					order,
				},
			}),
			providesTags: ['Engagement', 'EngagementStatus'],
		}),
		exportEngagementsToCSV: builder.mutation<
			{ fileUrl: string },
			SearchFilter &
				LanguageFilter &
				ProvidersFilter & {
					district?: string | null;
					start?: string | null;
					end?: string | null;
					archived?: boolean;
					fields?: string;
					schools?: string;
					timeZone?: string;
				}
		>({
			query: ({ search, languages, providers, schools, district, start, end, archived, timeZone, fields }) => ({
				url: `engagements/export/csv`,
				params: { search, languages, providers, schools, district, start, end, archived, timeZone, fields },
				responseType: 'json',
			}),
		}),
		importEngagements: builder.mutation<
			{ importedCount: number; success: any[]; errors: any[]; warnings: any[] },
			{ formData: FormData; districtId?: string | null; timezone: string }
		>({
			query: ({ formData, districtId, timezone }) => ({
				url: `import/engagements`,
				method: 'POST',
				body: formData,
				params: { district: districtId, timezone },
			}),
			invalidatesTags: ['Engagement'],
		}),
		getTemplateEngagementUrl: builder.query<{ url: string }, void>({
			query: () => ({
				url: 'import/engagements/template-url',
				method: 'GET',
			}),
		}),
		downloadFileBlob: builder.query<any, { url: string }>({
			query: (data) => {
				const encodedUrl = encodeURIComponent(data.url);

				return {
					url: `engagements/download-file/by-url`,
					params: { url: encodedUrl },
					responseHandler: async (response) => response.blob(),
				};
			},
		}),
		getLanguageDistribution: builder.query<
			LanguageDistributionDto[],
			{
				start?: string | null;
				end?: string | null;
				schools?: string | null;
				district?: string | null;
				services?: string | null;
				programs?: string | null;
				languages?: string | null;
				engagementTypes?: string | null;
				engagementStatuses?: string | null;
				providers?: string | null;
			} & DistrictFilter
		>({
			query: ({
				start,
				end,
				schools,
				district,
				districts,
				services,
				programs,
				languages,
				engagementStatuses,
				engagementTypes,
				providers,
			}) => ({
				url: `engagements/families/languages`,
				params: {
					start,
					end,
					schools,
					district,
					districts,
					services,
					programs,
					languages,
					engagementStatuses,
					engagementTypes,
					providers,
				},
			}),
			extraOptions: {
				skipAuth: true,
			},
			providesTags: ['Engagement'],
		}),
		getAttendanceLanguage: builder.query<
			BarChartDto[],
			{ start?: string | null; end?: string | null; schools?: string | null; district?: string | null } & DistrictFilter
		>({
			query: ({ start, end, schools, district, districts }) => ({
				url: `engagements/attendance/language`,
				params: { start, end, schools, district, districts },
			}),
			extraOptions: {
				skipAuth: true,
			},
			providesTags: ['Engagement'],
		}),
		getProvidersPerLanguage: builder.query<
			LanguagePerProvidersDto[],
			{
				chartType?: ProviderChartTypeEnum;
				start?: string | null;
				end?: string | null;
				schools?: string | null;
				district?: string | null;
				services?: string | null;
				programs?: string | null;
				languages?: string | null;
				engagementTypes?: string | null;
				engagementStatuses?: string | null;
				providers?: string | null;
			} & DistrictFilter
		>({
			query: ({
				chartType,
				start,
				end,
				schools,
				district,
				districts,
				services,
				programs,
				languages,
				engagementStatuses,
				engagementTypes,
				providers,
			}) => ({
				url: `engagements/providers/languages`,
				params: {
					chartType,
					start,
					end,
					schools,
					district,
					districts,
					services,
					programs,
					languages,
					engagementStatuses,
					engagementTypes,
					providers,
				},
			}),
			extraOptions: {
				skipAuth: true,
			},
			providesTags: ['Engagement'],
		}),
		getEngagementsStatistic: builder.query<
			EngagementStatsDto,
			{
				start?: string | null;
				end?: string | null;
				schools?: string | null;
				district?: string | null;
				services?: string | null;
				programs?: string | null;
				languages?: string | null;
				engagementTypes?: string | null;
				engagementStatuses?: string | null;
				providers?: string | null;
			} & DistrictFilter
		>({
			query: ({
				start,
				end,
				schools,
				district,
				districts,
				services,
				programs,
				languages,
				engagementStatuses,
				engagementTypes,
				providers,
			}) => ({
				url: `engagements/statistic/summary`,
				params: {
					start,
					end,
					schools,
					district,
					districts,
					services,
					programs,
					languages,
					engagementStatuses,
					engagementTypes,
					providers,
				},
			}),
			extraOptions: {
				skipAuth: true,
			},
			providesTags: ['Engagement', 'EngagementStatus'],
		}),
		getEngagementsStatusesSummary: builder.query<
			any,
			{
				start?: string | null;
				end?: string | null;
				schools?: string | null;
				district?: string | null;
				services?: string | null;
				programs?: string | null;
				languages?: string | null;
				engagementTypes?: string | null;
				engagementStatuses?: string | null;
				providers?: string | null;
			} & DistrictFilter
		>({
			query: ({
				start,
				end,
				schools,
				district,
				districts,
				services,
				programs,
				languages,
				engagementStatuses,
				engagementTypes,
				providers,
			}) => ({
				url: `engagements/statuses/summary`,
				params: {
					start,
					end,
					schools,
					district,
					districts,
					services,
					programs,
					languages,
					engagementStatuses,
					engagementTypes,
					providers,
				},
			}),
			extraOptions: {
				skipAuth: true,
			},
			providesTags: ['Engagement', 'EngagementStatus'],
		}),
		bulkDeleteEngagements: builder.mutation<EngagementDto[], { ids: string[] }>({
			query: ({ ids }) => ({
				url: `engagements`,
				method: 'DELETE',
				body: { ids },
			}),
			invalidatesTags: ['Engagement'],
		}),
		bulkArchiveEngagements: builder.mutation<EngagementDto[], { ids: string[] }>({
			query: ({ ids }) => ({
				url: `engagements/archive/bulk`,
				method: 'PUT',
				body: { ids },
			}),
			invalidatesTags: ['Engagement'],
		}),
		updateBulkEngagements: builder.mutation<EngagementDto[], { ids: string[]; updateData: any }>({
			query: ({ ids, updateData }) => ({
				url: 'engagements/edit/bulk',
				method: 'PUT',
				body: { ids, updateData },
			}),
			invalidatesTags: ['Engagement', 'Survey', 'SurveyAnswer'],
		}),
		getEngagementChartData: builder.query<
			any,
			{
				chartType?: EngagementChartTypeEnum;
				start?: string | null;
				end?: string | null;
				schools?: string | null;
				district?: string | null;
				services?: string | null;
				programs?: string | null;
				languages?: string | null;
				engagementTypes?: string | null;
				engagementStatuses?: string | null;
				providers?: string | null;
			} & DistrictFilter
		>({
			query: ({
				start,
				end,
				schools,
				district,
				districts,
				chartType,
				services,
				programs,
				languages,
				engagementStatuses,
				engagementTypes,
				providers,
			}) => ({
				url: `engagements/charts/count-data`,
				params: {
					chartType,
					start,
					end,
					schools,
					district,
					districts,
					services,
					programs,
					languages,
					engagementStatuses,
					engagementTypes,
					providers,
				},
			}),
			extraOptions: {
				skipAuth: true,
			},
			providesTags: ['Engagement', 'EngagementStatus'],
		}),
		getEngagementPieChartData: builder.query<
			any,
			{
				start?: string | null;
				end?: string | null;
				schools?: string | null;
				district?: string | null;
				languages?: string | null;
				providers?: string | null;
				archived?: boolean | null;
			} & DistrictFilter
		>({
			query: ({ start, end, schools, district, districts, languages, providers, archived }) => ({
				url: `engagements/charts/pie-chart/data`,
				params: {
					start,
					end,
					schools,
					district,
					districts,
					languages,
					providers,
					archived,
				},
			}),
			extraOptions: {
				skipAuth: true,
			},
			providesTags: ['Engagement', 'EngagementStatus'],
		}),
		getActiveEngagementCount: builder.query<
			number,
			{
				start?: string | null;
				end?: string | null;
				schools?: string | null;
				district?: string | null;
				languages?: string | null;
				providers?: string | null;
				archived?: boolean | null;
			} & DistrictFilter
		>({
			query: ({ start, end, schools, district, districts, languages, providers, archived }) => ({
				url: `engagements/counts/active`,
				params: {
					start,
					end,
					schools,
					district,
					districts,
					languages,
					providers,
					archived,
				},
			}),
			extraOptions: {
				skipAuth: true,
			},
			providesTags: ['Engagement', 'EngagementStatus'],
		}),

		getActiveEngagementCountLinguistLink: builder.query<
			number,
			{
				start?: string | null;
				end?: string | null;
				district?: string | null;
				languages?: string | null;
				archived?: boolean;
			}
		>({
			query: ({ start, end, district, languages, archived }) => ({
				url: `linguist-link/engagement/counts/active`,
				params: {
					start,
					end,
					district,
					languages,
					archived,
				},
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		getActiveEngagementStatisticLinguistLink: builder.query<
			number,
			{
				start?: string | null;
				end?: string | null;
				district?: string | null;
				languages?: string | null;
				archived?: boolean;
			}
		>({
			query: ({ start, end, district, languages, archived }) => ({
				url: `linguist-link/engagements/charts/pie-chart/data`,
				params: {
					start,
					end,
					district,
					languages,
					archived,
				},
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		getAllEngagementStatuses: builder.query<EngagementStatusDto[], void>({
			query: () => ({
				url: `statuses`,
			}),
			providesTags: ['EngagementStatus'],
		}),

		getEngagementStatuses: builder.query<EngagementStatusDto[], void>({
			query: () => ({
				url: `statuses/engagement`,
			}),
			providesTags: ['EngagementStatus'],
		}),
		saveEngagementStatuses: builder.mutation<void, any>({
			query: (data) => ({
				url: `statuses/engagement/update-list`,
				method: 'PUT',
				body: data,
			}),
			invalidatesTags: ['EngagementStatus'],
		}),
		getCountEngagementsByStatus: builder.query<number, { status: string }>({
			query: ({ status }) => ({
				url: `engagements/count/by-status`,
				params: { status },
			}),
			providesTags: ['EngagementStatus'],
		}),
		deleteEngagementStatuses: builder.mutation<void, { id?: string | null }>({
			query: ({ id }) => ({
				url: `statuses/engagement/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['EngagementStatus'],
		}),
		getEngagementsFromLLByDistrictName: builder.query<
			PageDto<EngagementDto[]>,
			PageOptionsDto &
				SearchFilter &
				LanguageFilter &
				ProvidersFilter & {
					district?: string | null;

					withReminder?: boolean | null;
					start?: string | null;
					end?: string | null;
					linkedToFamily?: boolean | null;
					archived?: boolean;

					schools?: string;
				}
		>({
			query: ({
				district,
				page,
				take,
				orderField,
				order,
				search,
				languages,
				schools,
				providers,
				withReminder,
				start,
				end,
				linkedToFamily,
				archived,
			}) => ({
				url: `linguist-link`,
				params: {
					page,
					take,
					orderField,
					order,
					district,
					search,
					languages,
					schools,
					providers,
					withReminder,
					start,
					end,
					linkedToFamily,
					archived,
				},
			}),
		}),
		getEngagementFromLinguistLink: builder.query<
			EngagementDto,
			{ id?: string | null; engagementItemId?: string | null }
		>({
			query: ({ id, engagementItemId }) => {
				const base = `linguist-link/engagement/${id}`;
				return {
					url: engagementItemId ? `${base}/item/${engagementItemId}` : base,
				};
			},
		}),

		getSelectedEngagementsFromLL: builder.query<
			EngagementDto[],
			SearchFilter &
				LanguageFilter & {
					engagementsIds?: string[];
					district?: string | null;
					start?: string | null;
					end?: string | null;
					withReminder?: boolean | null;
					linkedToFamily?: boolean | null;
				}
		>({
			query: ({ district, engagementsIds, search, languages, start, end, withReminder, linkedToFamily }) => ({
				url: `linguist-link/selected-all`,
				params: {
					district,
					languages,
					engagementsIds,
					search,
					start,
					end,
					withReminder,
					linkedToFamily,
				},
			}),
		}),
		getLanguageDistributionLinguistLink: builder.query<
			LanguageDistributionDto[],
			{
				start?: string | null;
				end?: string | null;
				district?: string | null;
				languages?: string | null;
				engagementTypes?: string | null;
				engagementStatuses?: string | null;
			} & DistrictFilter
		>({
			query: ({ start, end, district, districts, languages, engagementStatuses, engagementTypes }) => ({
				url: `linguist-link/families/languages`,
				params: {
					start,
					end,
					district,
					districts,
					languages,
					engagementStatuses,
					engagementTypes,
				},
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		getEngagementStatusesLinguistLink: builder.query<
			any,
			{
				start?: string | null;
				end?: string | null;
				district?: string | null;
				languages?: string | null;
				engagementTypes?: string | null;
				engagementStatuses?: string | null;
			} & DistrictFilter
		>({
			query: ({ start, end, district, districts, languages, engagementStatuses, engagementTypes }) => ({
				url: `linguist-link/statuses/summary`,
				params: {
					start,
					end,
					district,
					districts,
					languages,
					engagementStatuses,
					engagementTypes,
				},
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),

		getEngagementStatisticLinguistLink: builder.query<
			EngagementStatsDto,
			{
				start?: string | null;
				end?: string | null;
				district?: string | null;
				languages?: string | null;
				engagementTypes?: string | null;
				engagementStatuses?: string | null;
			} & DistrictFilter
		>({
			query: ({ start, end, district, districts, languages, engagementStatuses, engagementTypes }) => ({
				url: `linguist-link/statistic/summary`,
				params: {
					start,
					end,
					district,
					districts,
					languages,
					engagementStatuses,
					engagementTypes,
				},
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		getEngagementChartDataLinguistLink: builder.query<
			any,
			{
				chartType?: EngagementChartTypeEnum;
				start?: string | null;
				end?: string | null;
				district?: string | null;
				languages?: string | null;
				engagementTypes?: string | null;
				engagementStatuses?: string | null;
			} & DistrictFilter
		>({
			query: ({ start, end, district, districts, chartType, languages, engagementStatuses, engagementTypes }) => ({
				url: `linguist-link/charts/count-data`,
				params: {
					chartType,
					start,
					end,
					district,
					districts,
					languages,
					engagementStatuses,
					engagementTypes,
				},
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		getProvidersPerLanguageLinguistLink: builder.query<
			LanguagePerProvidersDto[],
			{
				chartType?: ProviderChartTypeEnum;
				start?: string | null;
				end?: string | null;
				district?: string | null;
				languages?: string | null;
				engagementTypes?: string | null;
				engagementStatuses?: string | null;
			} & DistrictFilter
		>({
			query: ({ chartType, start, end, district, districts, languages, engagementStatuses, engagementTypes }) => ({
				url: `linguist-link/providers/languages`,
				params: {
					chartType,
					start,
					end,
					district,
					districts,
					languages,
					engagementStatuses,
					engagementTypes,
				},
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		sendRemindersLinguistLinkEngagements: builder.mutation<
			{ errors: { engagementId: string; engagementTitle: string; familyId?: string; message: string }[] },
			{ engagementIds: string[]; timeZone: string }
		>({
			query: (data) => ({
				url: `linguist-link/send`,
				method: 'POST',
				body: data,
			}),
			invalidatesTags: ['Engagement'],
		}),
		getStatusesLinguistLink: builder.query<{ _id: string; statusName: string }[], void>({
			query: () => ({
				url: `linguist-link/statuses`,
			}),
		}),
		getSchoolsLinguistLink: builder.query<{ _id: string; text: string }[], void>({
			query: () => ({
				url: `linguist-link/schools`,
			}),
		}),
	}),
});

export default engagementsApi;

export const {
	useGetEngagementsListQuery,
	useGetEngagementQuery,
	useCreateEngagementMutation,
	useUpdateEngagementMutation,
	useDeleteEngagementMutation,
	useGetEngagementsByFamilyIdQuery,
	useGetEngagementsByProviderIdQuery,
	useExportEngagementsToCSVMutation,
	useArchiveEngagementMutation,
	useUnarchiveEngagementMutation,
	useLazyGetEngagementsListQuery,
	useImportEngagementsMutation,
	useGetTemplateEngagementUrlQuery,
	useGenerateEngagementsCustomFieldFileUrlMutation,
	useLazyDownloadFileBlobQuery,
	useGetLanguageDistributionQuery,
	useGetAttendanceLanguageQuery,
	useGetProvidersPerLanguageQuery,
	useGetEngagementsStatisticQuery,
	useGetEngagementsStatusesSummaryQuery,
	useBulkDeleteEngagementsMutation,
	useBulkArchiveEngagementsMutation,
	useUpdateBulkEngagementsMutation,
	useGetEngagementChartDataQuery,
	useGetEngagementPieChartDataQuery,
	useGetActiveEngagementCountQuery,
	useGetEngagementStatusesQuery,
	useSaveEngagementStatusesMutation,
	useLazyGetCountEngagementsByStatusQuery,
	useDeleteEngagementStatusesMutation,
	useCreateEngagementsArrayMutation,
	useGetEngagementsFromLLByDistrictNameQuery,
	useGetEngagementFromLinguistLinkQuery,
	useGetActiveEngagementCountLinguistLinkQuery,
	useGetActiveEngagementStatisticLinguistLinkQuery,
	useGetSelectedEngagementsFromLLQuery,
	useSendRemindersLinguistLinkEngagementsMutation,
	useGetLanguageDistributionLinguistLinkQuery,
	useGetEngagementStatusesLinguistLinkQuery,
	useGetEngagementStatisticLinguistLinkQuery,
	useGetEngagementChartDataLinguistLinkQuery,
	useGetStatusesLinguistLinkQuery,
	useGetAllEngagementStatusesQuery,
	useGetProvidersPerLanguageLinguistLinkQuery,
	useGetEngagementsListUnifiedQuery,
	useGetSchoolsLinguistLinkQuery,
} = engagementsApi;
